import React, { useState, useEffect } from "react";
import { ClientToReview, PracticeReview } from "practice-reviews";
import { CircularProgress, TextField, Typography, Stack, Grid } from "@mui/material";
import { gql, useMutation } from "@apollo/client";
import { makeStyles } from "makeStyles";
import { useCurrentUser, Permissions } from "users";
import { useUnsavedChanges } from "UnsavedChangesProvider";
import { LoadingButton } from "@mui/lab";
import { useNotifications } from "notifications";
import { actionStyles } from "styles/common";

interface Props {
  practiceReviewId: number;
  clientsToReview: ClientToReview[];
  disableInputs?: boolean;
}

export const ClientsToReview: React.FunctionComponent<Props> = (props) => {
  const useStyles = makeStyles()((theme) => ({
    ...actionStyles(theme),
    root: {
      "& > :not(:first-child)": {
        marginTop: theme.spacing(1)
      },
      "& .MuiAutocomplete-tag": {
        width: "100%",
        justifyContent: "space-between",
        fontFamily: theme.typography.body1.fontFamily
      }
    },
    header: {
      display: "flex",
      alignItems: "center",
      "& > :not(:first-child)": {
        marginLeft: theme.spacing(1)
      }
    }
  }));

  const { classes } = useStyles();
  const { userHasPermission } = useCurrentUser();
  const notifications = useNotifications();
  const { unsavedChanges, changesSaved, setSaveFunction, unsavedChangesExist } = useUnsavedChanges();
  const changeKey = "clients to review";

  function getClientsToReviewAsString(clientsToReview: ClientToReview[]) {
    return clientsToReview && clientsToReview.length
      ? clientsToReview
          .map((pc) => pc.clientName)
          .sort()
          .join("\r\n")
      : undefined;
  }

  const [clientsToReviewString, setClientsToReviewString] = useState<string | undefined>(getClientsToReviewAsString(props.clientsToReview));

  const [saveClientsToReviewStringMutate, saveClientsToReviewStringMutation] = useMutation<
    { practiceReview: { saveClientsToReviewString: PracticeReview } },
    { practiceReviewId: number; clientsToReviewString: string }
  >(gql`
    mutation SaveClientsToReviewString($practiceReviewId: Int!, $clientsToReviewString: String!) {
      practiceReview {
        saveClientsToReviewString(practiceReviewId: $practiceReviewId, clientsToReviewString: $clientsToReviewString) {
          id
          clientsToReview {
            id
            clientName
          }
        }
      }
    }
  `);

  async function saveClients() {
    const result = await saveClientsToReviewStringMutate({
      variables: {
        practiceReviewId: props.practiceReviewId,
        clientsToReviewString: clientsToReviewString || ""
      }
    });
    if (result.data?.practiceReview.saveClientsToReviewString?.id) {
      notifications.success("Saved clients.");
      changesSaved(changeKey);
      setClientsToReviewString(getClientsToReviewAsString(result.data.practiceReview.saveClientsToReviewString.clientsToReview));

      return true;
    }

    return false;
  }

  // Hook up for unsaved changes
  useEffect(() => setSaveFunction(() => saveClients(), changeKey));

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h3">Clients to Review</Typography>
        {saveClientsToReviewStringMutation.loading && <CircularProgress size={22} />}
      </div>
      {userHasPermission(Permissions.PrUpdateEstimateAndClients) ? (
        <Stack spacing={1} justifyContent="flex-start" alignItems="flex-start">
          <TextField
            multiline
            fullWidth
            margin="none"
            value={clientsToReviewString}
            disabled={props.disableInputs || saveClientsToReviewStringMutation.loading}
            onChange={(e) => {
              setClientsToReviewString(e.target.value);
              unsavedChanges(changeKey);
            }}
          />
          <Grid container justifyContent="flex-end">
            <Grid item className={classes.actions}>
              <LoadingButton
                variant="outlined"
                color="primary"
                loading={saveClientsToReviewStringMutation.loading}
                onClick={() => saveClients()}
                disabled={props.disableInputs || !unsavedChangesExist(changeKey)}>
                {unsavedChangesExist(changeKey) ? "Save" : "Saved"}
              </LoadingButton>
            </Grid>
          </Grid>
        </Stack>
      ) : (
        <div style={{ whiteSpace: "pre-line" }}>{getClientsToReviewAsString(props.clientsToReview)}</div>
      )}
    </div>
  );
};
